var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "nativewind/jsx-runtime";
import { useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { throttle } from 'lodash';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { View } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { useMe } from '@/atoms/Auth';
import { recentlyUsedMobilityToolAtom } from '@/atoms/User';
import { getMobilityToolDefaultValue, } from '@/constant/review';
import { UpvoteTargetTypeDto, } from '@/generated-sources/openapi';
import { UploadProgressOverlay } from '@/components/UploadProgressOverlay';
import useAppComponents from '@/hooks/useAppComponents';
import { REGISTER_TIMEOUT_MS, REQUEST_ID_HEADER, useIdempotencyKey, } from '@/hooks/useIdempotencyKey';
import { showUploadFailedToast, useImageUploadWithProgress, } from '@/hooks/useImageUploadWithProgress';
import usePost from '@/hooks/usePost';
import { USER_TUTORIAL_PROGRESS_QUERY_KEY } from '@/hooks/useUserTutorialProgress';
import { updateSearchCacheForPlaceAsync } from '@/utils/SearchPlacesUtils';
import ToastUtils from '@/utils/ToastUtils';
import { SafeAreaWrapper } from '@/components/SafeAreaWrapper';
import PhotoConfirmBottomSheet from '@/modals/PhotoConfirmBottomSheet';
import { pushItemsAtom } from '@/screens/SearchScreen/atoms/quest';
import IndoorInfoSection from '../sections/IndoorInfoSection';
import PlaceInfoSection from '../sections/PlaceInfoSection';
import UserTypeSection from '../sections/UserTypeSection';
import VisitorReviewSection from '../sections/VisitorReviewSection';
import SectionSeparator from '../sections/SectionSeparator';
export default function IndoorReviewView({ place, gotoPlaceDetail, setMobilityTool, setReviewTypeToToilet, }) {
    const { api } = useAppComponents();
    const queryClient = useQueryClient();
    const { userInfo } = useMe();
    const { uploadImages, uploadProgress } = useImageUploadWithProgress();
    const getIdempotencyKey = useIdempotencyKey();
    const form = useForm({
        defaultValues: {
            mobilityTool: getMobilityToolDefaultValue(userInfo === null || userInfo === void 0 ? void 0 : userInfo.mobilityTools),
            recommendedMobilityTypes: new Set(),
            spaciousType: undefined,
            indoorPhotos: [],
            comment: '',
            seatTypes: new Set(),
            seatComment: '',
            orderMethods: new Set(),
            features: new Set(),
        },
    });
    const setRecentlyUsedMobilityTool = useSetAtom(recentlyUsedMobilityToolAtom);
    const pushItems = useSetAtom(pushItemsAtom);
    const [isPhotoModalVisible, setIsPhotoModalVisible] = useState(false);
    const pendingSubmitRef = useRef(null);
    const hasShownPhotoModalRef = useRef(false);
    const scrollViewRef = useRef(null);
    const visitorSectionY = useRef(0);
    const photoAreaY = useRef(0);
    function onInvalid(errors) {
        // 첫 번째 에러 필드의 메시지를 토스트로 표시
        const firstErrorField = Object.keys(errors)[0];
        const firstError = errors[firstErrorField];
        if (firstError === null || firstError === void 0 ? void 0 : firstError.message) {
            ToastUtils.show(firstError.message);
        }
    }
    function onValid(values) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (!((_a = values.indoorPhotos) === null || _a === void 0 ? void 0 : _a.length) && !hasShownPhotoModalRef.current) {
                pendingSubmitRef.current = () => registerPlace(values, gotoPlaceDetail);
                hasShownPhotoModalRef.current = true;
                setIsPhotoModalVisible(true);
                return;
            }
            registerPlace(values, gotoPlaceDetail);
        });
    }
    function onValidAfterToilet(values) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const afterSuccess = () => {
                setMobilityTool(values.mobilityTool);
                setReviewTypeToToilet();
            };
            if (!((_a = values.indoorPhotos) === null || _a === void 0 ? void 0 : _a.length) && !hasShownPhotoModalRef.current) {
                pendingSubmitRef.current = () => registerPlace(values, afterSuccess);
                hasShownPhotoModalRef.current = true;
                setIsPhotoModalVisible(true);
                return;
            }
            registerPlace(values, afterSuccess);
        });
    }
    const handlePhotoModalConfirm = useCallback(() => {
        // 사진 추가하기 - 바텀시트 닫고 사진 영역으로 스크롤
        setIsPhotoModalVisible(false);
        pendingSubmitRef.current = null;
        setTimeout(() => {
            var _a;
            const targetY = visitorSectionY.current + photoAreaY.current;
            (_a = scrollViewRef.current) === null || _a === void 0 ? void 0 : _a.scrollToPosition(0, targetY, true);
        }, 300);
    }, []);
    function handlePhotoModalCancel() {
        var _a;
        // 사진 없이 등록하기 - 등록 진행
        setIsPhotoModalVisible(false);
        (_a = pendingSubmitRef.current) === null || _a === void 0 ? void 0 : _a.call(pendingSubmitRef);
        pendingSubmitRef.current = null;
    }
    const submitMutation = usePost(['PlaceReview', 'Indoor', 'Submit'], (_a) => __awaiter(this, [_a], void 0, function* ({ values, uploaded, requestId, }) {
        return submitRegistration({
            api,
            queryClient,
            placeId: place === null || place === void 0 ? void 0 : place.id,
            values,
            uploaded,
            requestId,
        });
    }));
    const registerPlace = useMemo(() => throttle((values, afterSuccess) => __awaiter(this, void 0, void 0, function* () {
        if (submitMutation.isPending) {
            return;
        }
        setRecentlyUsedMobilityTool({
            name: values.mobilityTool,
            timestamp: Date.now(),
        });
        // 사진 URL 은 업로드마다 달라지므로 업로드 전 입력(로컬 사진 포함)으로 키를 정한다.
        const requestId = getIdempotencyKey(values);
        let uploaded;
        try {
            uploaded = yield uploadAllPhotos({ api, values, uploadImages });
        }
        catch (e) {
            showUploadFailedToast(e);
            return;
        }
        const registered = yield submitMutation.mutateAsync({
            values,
            uploaded,
            requestId,
        });
        if (!registered.success) {
            return;
        }
        if (registered.data) {
            pushItems(registered.data);
        }
        ToastUtils.show('리뷰를 등록했어요.');
        afterSuccess();
    }), 1000), [
        api,
        place,
        uploadImages,
        getIdempotencyKey,
        setRecentlyUsedMobilityTool,
        submitMutation,
        queryClient,
        pushItems,
    ]);
    return (_jsx(FormProvider, Object.assign({}, form, { children: _jsxs(SafeAreaWrapper, { edges: ['bottom'], children: [_jsxs(KeyboardAwareScrollView, { ref: scrollViewRef, stickyHeaderIndices: [0], contentContainerClassName: "grow", children: [_jsx(View, { className: "border-b border-gray-20", children: _jsx(PlaceInfoSection, { name: place === null || place === void 0 ? void 0 : place.name, address: place === null || place === void 0 ? void 0 : place.address }) }), _jsx(SectionSeparator, {}), _jsx(UserTypeSection, { nickname: userInfo === null || userInfo === void 0 ? void 0 : userInfo.nickname }), _jsx(SectionSeparator, {}), _jsx(View, { onLayout: e => {
                                visitorSectionY.current = e.nativeEvent.layout.y;
                            }, children: _jsx(VisitorReviewSection, { onPhotoSectionLayout: y => {
                                    photoAreaY.current = y;
                                } }) }), _jsx(SectionSeparator, {}), _jsx(IndoorInfoSection, { onSave: form.handleSubmit(onValid, onInvalid), onSaveAndToiletReview: form.handleSubmit(onValidAfterToilet, onInvalid) })] }), _jsx(PhotoConfirmBottomSheet, { isVisible: isPhotoModalVisible, onConfirm: handlePhotoModalConfirm, onCancel: handlePhotoModalCancel }), _jsx(UploadProgressOverlay, Object.assign({}, uploadProgress))] }) })));
}
function uploadAllPhotos(_a) {
    return __awaiter(this, arguments, void 0, function* ({ api, values, uploadImages, }) {
        const images = yield uploadImages(api, values.indoorPhotos, 'PLACE_REVIEW');
        return { images };
    });
}
function submitRegistration(_a) {
    return __awaiter(this, arguments, void 0, function* ({ api, queryClient, placeId, values, uploaded, requestId, }) {
        var _b;
        try {
            const res = yield api.registerPlaceReviewPost({
                placeId,
                mobilityTool: values.mobilityTool,
                recommendedMobilityTypes: [...values.recommendedMobilityTypes],
                spaciousType: values.spaciousType,
                comment: values.comment,
                seatTypes: values.seatComment
                    ? [...values.seatTypes, values.seatComment]
                    : [...values.seatTypes],
                orderMethods: [...values.orderMethods],
                features: [...values.features],
                imageUrls: uploaded.images,
            }, { headers: { [REQUEST_ID_HEADER]: requestId }, timeout: REGISTER_TIMEOUT_MS });
            queryClient.invalidateQueries({
                queryKey: ['PlaceDetailV2', placeId, UpvoteTargetTypeDto.PlaceReview],
            });
            queryClient.invalidateQueries({
                queryKey: ['MyReviews', UpvoteTargetTypeDto.PlaceReview],
            });
            queryClient.invalidateQueries({
                queryKey: ['ReviewsUpvoted', UpvoteTargetTypeDto.PlaceReview],
            });
            queryClient.invalidateQueries({
                queryKey: ['ReviewHistory', 'Review', UpvoteTargetTypeDto.PlaceReview],
            });
            queryClient.invalidateQueries({
                queryKey: ['ReviewHistory', 'Upvote', UpvoteTargetTypeDto.PlaceReview],
            });
            // 윌리의 외출 NUX 튜토리얼: 리뷰 작성 미션 진행 상태 무효화
            queryClient.invalidateQueries({
                queryKey: USER_TUTORIAL_PROGRESS_QUERY_KEY,
            });
            updateSearchCacheForPlaceAsync(api, queryClient, placeId);
            return {
                success: true,
                data: (_b = res.data.contributedChallengeInfos) === null || _b === void 0 ? void 0 : _b.flatMap(info => info.completedQuestsByContribution.map(quest => ({
                    challengeId: info.challenge.id,
                    type: quest.completeStampType,
                    title: quest.title,
                }))),
            };
        }
        catch (error) {
            ToastUtils.showOnApiError(error);
            return {
                success: false,
            };
        }
    });
}
